
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";

import { EditorState } from "@codemirror/state";
import { EditorView } from "@codemirror/view";

import { uiStore } from "@/store/store";

import { createBaseExtensions } from "@/lib/code-editor/base-extensions";
import { updateErrorLine } from "@/lib/code-editor/error-line-highlighter";

import { CodeInputLanguage } from "@/components/Reusable/CustomInputs/CodeInput/CodeInput.vue";

@Component
export default class CodeMirrorWrapper extends Vue {
  @Prop()
  value!: string | null;

  @Prop()
  autofillOnEmpty!: string | null;

  @Prop()
  language!: CodeInputLanguage;

  @Prop({ default: true })
  showLineNumbers!: boolean;

  @Prop({ default: 1 })
  firstLineNumber!: number;

  @Prop({ default: false })
  readOnly!: boolean;

  @Prop({ default: "" })
  placeholder!: string;

  @Prop({ default: null })
  focusAtPosition!: number | null;

  @Prop({ default: () => [] })
  twigVariables!: string[];

  @Prop({ default: () => [] })
  tags!: string[];

  @Prop({ default: null })
  highlightErrorLine!: number | null;

  @Prop({ default: false })
  formatting!: boolean;

  private editorView: EditorView | null = null;
  private isUpdatingFromProp = false;

  get classList() {
    return {
      "breakdance-codemirror": true,
      "breakdance-codemirror-readonly": this.readOnly
    };
  }

  get disableLineWrapping() {
    return uiStore.settings.disableCodeMirrorLineWrapping;
  }

  @Watch("disableLineWrapping")
  onDisableLineWrappingChange() {
    this.destroyEditor();
    this.initEditor();
  }

  @Watch("value")
  onValueChange(newValue: string | null) {
    if (this.editorView && newValue !== this.editorView.state.doc.toString()) {
      this.isUpdatingFromProp = true;
      this.editorView.dispatch({
        changes: {
          from: 0,
          to: this.editorView.state.doc.length,
          insert: newValue || ""
        }
      });
      this.isUpdatingFromProp = false;
    }
  }

  @Watch("highlightErrorLine")
  onHighlightErrorLineChange(newLine: number | null) {
    if (this.editorView) {
      updateErrorLine(this.editorView, newLine);
    }
  }

  onChange(value: string) {
    this.$emit("input", value);
  }

  async onFormat() {
    this.$emit("format");
  }

  onCursorPositionChange(position: number) {
    this.$emit("cursor-position", position);
  }

  initEditor() {
    const extensions = createBaseExtensions(
      this.language,
      this.onCursorPositionChange,
      this.onChange,
      this.onFormat,
      this.isUpdatingFromProp,
      this.placeholder,
      this.showLineNumbers,
      this.firstLineNumber,
      this.readOnly,
      this.disableLineWrapping,
      this.focusAtPosition,
      this.twigVariables,
      this.tags,
      this.autofillOnEmpty,
      this.highlightErrorLine
    );

    this.editorView = new EditorView({
      parent: this.$refs.editor as Element,
      state: EditorState.create({
        doc: this.value || "",
        extensions
      })
    });
  }

  destroyEditor() {
    if (this.editorView) {
      this.editorView.destroy();
      this.editorView = null;
    }
  }

  beforeDestroy() {
    this.destroyEditor();
  }

  mounted() {
    this.initEditor();
  }
}
